import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Play,
  SkipForward,
  ArrowRight,
  Users,
  Crown,
  Flame,
  Zap,
  LogOut,
  Copy,
  Check,
  UserX,
  Coins,
  WifiOff,
  Loader2,
  Eye,
  Gamepad2,
  Sparkles,
  Clock,
  CheckCircle2,
  Circle,
  Bot,
} from 'lucide-react';

import type {
  PublicRoom,
  PublicQuestion,
  TeamStanding,
  RevealPayload,
  AnswerPayload,
  FriendSummary,
} from '../../shared/protocol';
import type { ConnectionStatus } from '../lib/realtime';
import { BRAND, TEAM_COLORS, TEAM_IDS, type TeamId } from '../brand';
import { Avatar } from './Avatar';
import { AppIcon } from './Logo';
import { MonitorPanel } from './MonitorPanel';
import { startRecording } from '../lib/recorder';
import { realtime } from '../lib/realtime';
import { MonitorConsent } from './MonitorConsent';
import { MultipleChoiceQuestion } from './questions/MultipleChoiceQuestion';
import { TrueFalseQuestion } from './questions/TrueFalseQuestion';
import { MultiSelectQuestion } from './questions/MultiSelectQuestion';
import { FillBlankQuestion } from './questions/FillBlankQuestion';
import { MatchPairsQuestion, type PairLink } from './questions/MatchPairsQuestion';
import { InviteFriendsPanel } from './InviteFriends';
import { AudioControls } from './AudioControls';
import {
  startMusic,
  stopMusic,
  playTickSound,
  playSelectSound,
  playRevealSting,
  playCorrectSound,
  playWrongSound,
} from '../lib/sound';
import {
  StartTimePicker,
  DEFAULT_START,
  startAtFromChoice,
  useCountdown,
  formatCountdown,
  localClockOf,
  type StartChoice,
} from './StartTimePicker';

interface Props {
  room: PublicRoom;
  question: PublicQuestion | null;
  questionEndsAt: number;
  countdown: number | null;
  reveal: RevealPayload | null;
  myId: string;
  connection: ConnectionStatus;
  serverNow: () => number;
  onStart: () => void;
  onNext: () => void;
  onSkip: () => void;
  onKick: (playerId: string) => void;
  onSetTeam: (team: string) => void;
  onAnswer: (payload: AnswerPayload) => void;
  onLeave: () => void;
  /** Salas del servidor: marcar que estás listo. */
  onReady: (ready: boolean) => void;
  /** Fija o quita la hora de inicio (anfitrión, o quien creó una sala del servidor). */
  onSchedule: (startAt: number | null) => void;
  /** Amigos de la cuenta, para invitarlos; null sin cuenta o mientras cargan. */
  friends: FriendSummary[] | null;
  canInvite: boolean;
  onManageFriends: () => void;
}

export const LiveRoom: React.FC<Props> = ({
  room,
  question,
  questionEndsAt,
  countdown,
  reveal,
  myId,
  connection,
  serverNow,
  onStart,
  onNext,
  onSkip,
  onKick,
  onSetTeam,
  onAnswer,
  onLeave,
  onReady,
  onSchedule,
  friends,
  canInvite,
  onManageFriends,
}) => {
  const isHost = room.hostId === myId;
  /** Sin anfitrión humano, las pausas entre preguntas se cuentan en pantalla. */
  const autoLeft = useCountdown(room.serverHosted ? room.autoAt : null);
  const me = room.players.find((p) => p.id === myId);
  /** Sala supervisada: se graba la pantalla de los jugadores (no la del anfitrión). */
  const monitored = room.settings.monitorPlayers;

  // Consentimiento: en una sala supervisada nadie es grabado hasta que acepta
  // (ver MonitorConsent). Se recuerda por sala en la pestaña, para que recargar
  // la página no lo vuelva a pedir.
  const consentKey = `dahu_rec_consent_${room.pin}`;
  const [accepted, setAccepted] = useState(() => {
    try {
      return sessionStorage.getItem(consentKey) === '1';
    } catch {
      return false;
    }
  });
  /** El servidor confirma si a esta cuenta se le graba (a los menores no). */
  const [recorded, setRecorded] = useState(false);

  const acceptRecording = () => {
    try {
      sessionStorage.setItem(consentKey, '1');
    } catch {
      /* sin almacenamiento: valdrá mientras no se recargue */
    }
    setAccepted(true);
  };

  useEffect(() => realtime.on((msg) => {
    if (msg.t === 'rec:consent') setRecorded(msg.recorded);
  }), []);

  useEffect(() => {
    if (monitored && !isHost && accepted) realtime.send({ t: 'rec:consent' });
  }, [monitored, isHost, accepted]);

  // Se graba mientras el jugador está en la sala; al salir o llegar al podio
  // se corta y se envía lo pendiente.
  useEffect(() => {
    if (!monitored || isHost || !accepted || !recorded) return;
    return startRecording();
  }, [monitored, isHost, accepted, recorded]);

  /* ---------------- respuesta en curso ---------------- */

  const [choice, setChoice] = useState<string | undefined>();
  const [multi, setMulti] = useState<string[]>([]);
  const [text, setText] = useState('');
  const [links, setLinks] = useState<PairLink[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [confirmLeave, setConfirmLeave] = useState(false);

  // Cada pregunta nueva parte de cero.
  useEffect(() => {
    setChoice(undefined);
    setMulti([]);
    setText('');
    setLinks([]);
    setSubmitted(false);
  }, [question?.id]);

  const send = (payload: AnswerPayload) => {
    if (submitted) return;
    setSubmitted(true);
    playSelectSound();
    onAnswer(payload);
  };

  /* ---------------- cronómetro ---------------- */

  const [secondsLeft, setSecondsLeft] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (room.status !== 'question' || !questionEndsAt) {
      setSecondsLeft(0);
      return;
    }
    const tick = () => {
      // La cuenta atrás se deriva del reloj del servidor, no del local.
      const remaining = Math.max(0, (questionEndsAt - serverNow()) / 1000);
      setSecondsLeft(remaining);
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [room.status, questionEndsAt, serverNow]);

  const progress = question ? Math.max(0, secondsLeft / question.timeLimit) : 0;
  const urgent = secondsLeft <= 5 && secondsLeft > 0;

  // El anfitrión entra en la cuenta si eligió jugar, o si está solo en la sala.
  const guests = room.players.filter((p) => p.id !== room.hostId);
  const hostCompetes = room.settings.hostPlays || guests.length === 0;
  const contenders = hostCompetes ? room.players : guests;
  const answeredCount = contenders.filter((p) => p.hasAnswered).length;
  const contenderCount = contenders.length;
  /** Quien mira sin jugar ve las opciones pero no puede contestar. */
  const canAnswer = !isHost || hostCompetes;

  /* ---------------- sonido ---------------- */

  const questionKey = question ? `${question.id}:${question.index}` : '';
  const revealed = room.status === 'reveal' && reveal !== null;
  const waitingConsent = monitored && !isHost && !accepted;

  // La música suena desde que se entra en la sala hasta el resultado de cada
  // pregunta; con cada pregunta nueva vuelve a empezar desde el principio.
  useEffect(() => {
    if (waitingConsent) return;
    if (room.status === 'lobby' || room.status === 'countdown') startMusic();
  }, [room.status, waitingConsent]);

  useEffect(() => {
    if (waitingConsent || room.status !== 'question' || !questionKey) return;
    startMusic({ restart: true });
  }, [room.status, questionKey, waitingConsent]);

  // Al dar el resultado: golpe, se corta la música y luego acierto o fallo.
  useEffect(() => {
    if (!revealed || waitingConsent) return;
    stopMusic(90);
    playRevealSting();
    const you = reveal?.you;
    if (!you && !canAnswer) return;
    const t = setTimeout(() => (you?.isCorrect ? playCorrectSound() : playWrongSound()), 380);
    return () => clearTimeout(t);
    // Solo al pasar a "revelado" de cada pregunta.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealed, questionKey]);

  // Últimos 5 segundos: un tic por segundo, más agudo cuanto menos queda.
  // Solo cuando el reloj baja un segundo: al empezar una pregunta, el primer
  // cálculo puede salir del cronómetro anterior y no debe sonar.
  const wholeSecondsLeft = Math.ceil(secondsLeft);
  const prevSecondRef = useRef(wholeSecondsLeft);
  useEffect(() => {
    const prev = prevSecondRef.current;
    prevSecondRef.current = wholeSecondsLeft;
    if (room.status !== 'question' || waitingConsent) return;
    if (wholeSecondsLeft !== prev - 1 || wholeSecondsLeft > 5 || wholeSecondsLeft < 1) return;
    playTickSound((5 - wholeSecondsLeft) / 4);
  }, [wholeSecondsLeft, room.status, waitingConsent]);

  // Fuera de la sala (salir o llegar al podio) no queda nada sonando.
  useEffect(() => () => stopMusic(250), []);

  /* ================= CONSENTIMIENTO ================= */

  if (monitored && !isHost && !accepted) {
    return (
      <MonitorConsent
        hostName={room.players.find((p) => p.id === room.hostId)?.displayName ?? 'El anfitrión'}
        onAccept={acceptRecording} onLeave={onLeave} />
    );
  }

  /* ================= LOBBY ================= */

  if (room.status === 'lobby') {
    return (
      <LobbyView
        room={room}
        isHost={isHost}
        myId={myId}
        connection={connection}
        onStart={onStart}
        onKick={onKick}
        onSetTeam={onSetTeam}
        onLeave={onLeave}
        onReady={onReady}
        onSchedule={onSchedule}
        friends={friends}
        canInvite={canInvite}
        onManageFriends={onManageFriends}
      />
    );
  }

  /* ================= CUENTA ATRÁS ================= */

  if (room.status === 'countdown') {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center gap-6 relative">
        <AudioControls labeled align="right" className="absolute top-4 right-4" />
        <PinBadge pin={room.pin} />
        <p className="text-ink-400 uppercase tracking-[0.2em] text-sm">Prepárate</p>
        <div
          key={countdown}
          className="animate-pop text-[8rem] md:text-[12rem] leading-none font-display font-black text-brand-gradient"
        >
          {countdown && countdown > 0 ? countdown : '¡Ya!'}
        </div>
        <p className="text-ink-300">{room.quizTitle}</p>
      </div>
    );
  }

  /* ================= PREGUNTA / REVELACIÓN ================= */

  return (
    <div className="no-select-game max-w-6xl mx-auto px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
      {connection !== 'online' && <ConnectionBanner status={connection} />}
      {monitored && !isHost && <MonitorNotice isHost={false} compact />}
      {monitored && isHost && <MonitorPanel room={room} />}
      <PinBadge pin={room.pin} />

      {/* Cabecera: salida, progreso, marcador y cronómetro.

          En una sola fila también en el teléfono: repartida en dos, el
          enunciado bajaba y las opciones quedaban fuera del pulgar. Lo que
          sobra en pantalla estrecha se abrevia en vez de saltar de línea. */}
      <div className="flex items-center gap-2 sm:gap-4">
        {/* La salida tiene que estar disponible durante toda la partida.
            Sin ella, la única forma de abandonar es cerrar la pestaña. */}
        <button
          onClick={() => setConfirmLeave(true)}
          className="p-2 -ml-2 rounded-lg text-ink-500 hover:text-ink-100 hover:bg-ink-800 transition-colors shrink-0"
          aria-label="Salir de la partida"
          title="Salir de la partida"
        >
          <LogOut className="w-5 h-5" />
        </button>
        <AudioControls className="-ml-1 sm:ml-0" />
        <span className="chip border-ink-700 bg-ink-850 text-ink-300 shrink-0 tabular-nums">
          <span className="hidden sm:inline">Pregunta </span>
          {(question?.index ?? 0) + 1}
          <span className="hidden sm:inline"> de </span>
          <span className="sm:hidden">/</span>
          {question?.total ?? room.totalQuestions}
        </span>

        <div className="flex items-center gap-1.5 sm:gap-3 ml-auto min-w-0">
          {me && (
            <span className="chip pl-1 border-ember-600 bg-ember-500/10 text-ember-300 tabular-nums">
              <Avatar config={me.avatar} size={20} className="shadow-none" />
              {me.score.toLocaleString('es')}
              <span className="hidden sm:inline">pts</span>
            </span>
          )}
          {me && me.streak > 1 && (
            <span className="chip border-ember-500 bg-ember-500/15 text-ember-300">
              <Flame className="w-3.5 h-3.5" /> {me.streak}
            </span>
          )}
          <span
            className="chip border-ink-700 bg-ink-850 text-ink-400 tabular-nums"
            title="Han contestado"
          >
            <Users className="w-3.5 h-3.5" /> {answeredCount}/{contenderCount}
          </span>
        </div>

        {room.status === 'question' && (
          <div
            className={`flex items-center justify-center min-w-12 px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl border-2
                        font-mono text-xl sm:text-2xl font-bold tabular-nums shrink-0 ${
              urgent
                ? 'border-danger-500 bg-danger-500/15 text-danger-400 animate-urgent'
                : 'border-ink-700 bg-ink-850 text-ink-100'
            }`}
            role="timer"
            aria-live="off"
          >
            {Math.ceil(secondsLeft)}
          </div>
        )}
      </div>

      {/* Barra de tiempo */}
      {room.status === 'question' && (
        <div className="h-2 rounded-full bg-ink-800 overflow-hidden">
          <div
            className={`h-full rounded-full transition-none ${
              urgent ? 'bg-danger-500' : 'bg-brand-gradient'
            }`}
            style={{ width: `${progress * 100}%` }}
          />
        </div>
      )}

      {/* Enunciado */}
      {question && (
        <div className="text-center space-y-4">
          <h2 className="text-xl sm:text-2xl md:text-4xl font-bold text-ink-50 text-balance leading-tight">
            {question.title}
          </h2>
          {question.mediaUrl && (
            <img
              src={question.mediaUrl}
              alt=""
              className="max-h-64 mx-auto rounded-2xl border border-ink-700 object-contain"
            />
          )}
          {question.pointsMultiplier === 2 && (
            <span className="chip border-ember-500 bg-ember-500/15 text-ember-300">
              <Zap className="w-3.5 h-3.5" /> Puntos dobles
            </span>
          )}
          {question.pointsMultiplier === 0 && (
            <span className="chip border-ink-600 bg-ink-800 text-ink-400">Sin puntos</span>
          )}
        </div>
      )}

      {/* Espera tras contestar */}
      {submitted && !revealed && (
        <WaitingForOthers answered={answeredCount} total={contenderCount} />
      )}

      {/* Resultado personal */}
      {revealed && reveal.you && <PersonalResult you={reveal.you} />}
      {revealed && !reveal.you && canAnswer && (
        <p className="text-center text-ink-400">No contestaste esta pregunta.</p>
      )}

      {/* Área de respuesta */}
      {question && (
        <div>
          {question.type === 'multiple_choice' && (
            <MultipleChoiceQuestion
              options={question.options ?? []}
              selectedOptionId={choice}
              disabled={!canAnswer || submitted || revealed}
              correctOptionIds={revealed ? reveal.correctOptionIds : undefined}
              distribution={revealed && isHost ? reveal.distribution : undefined}
              totalAnswers={revealed ? reveal.answeredCount : 0}
              onSelect={(id) => {
                setChoice(id);
                send({ kind: 'choice', optionId: id });
              }}
            />
          )}

          {question.type === 'true_false' && (
            <TrueFalseQuestion
              options={question.options ?? []}
              selectedOptionId={choice}
              disabled={!canAnswer || submitted || revealed}
              correctOptionIds={revealed ? reveal.correctOptionIds : undefined}
              distribution={revealed && isHost ? reveal.distribution : undefined}
              totalAnswers={revealed ? reveal.answeredCount : 0}
              onSelect={(id) => {
                setChoice(id);
                send({ kind: 'choice', optionId: id });
              }}
            />
          )}

          {question.type === 'multi_select' && (
            <MultiSelectQuestion
              options={question.options ?? []}
              selectedIds={multi}
              submitted={submitted}
              disabled={!canAnswer || revealed}
              correctOptionIds={revealed ? reveal.correctOptionIds : undefined}
              onToggle={(id) =>
                setMulti((prev) =>
                  prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
                )
              }
              onSubmit={() => send({ kind: 'multi', optionIds: multi })}
            />
          )}

          {question.type === 'fill_blank' && (
            <FillBlankQuestion
              value={text}
              onChange={setText}
              submitted={submitted}
              disabled={!canAnswer || revealed}
              acceptedAnswers={revealed ? reveal.acceptedAnswers : undefined}
              isCorrect={reveal?.you?.isCorrect}
              onSubmit={() => send({ kind: 'text', text })}
            />
          )}

          {question.type === 'match_pairs' && (
            <MatchPairsQuestion
              leftItems={question.leftItems ?? []}
              rightItems={question.rightItems ?? []}
              links={links}
              submitted={submitted}
              disabled={!canAnswer || revealed}
              correctPairs={revealed ? reveal.correctPairs : undefined}
              onLink={(leftId, rightId) =>
                setLinks((prev) => [...prev.filter((l) => l.leftId !== leftId), { leftId, rightId }])
              }
              onUnlink={(leftId) => setLinks((prev) => prev.filter((l) => l.leftId !== leftId))}
              onSubmit={() => send({ kind: 'pairs', links })}
            />
          )}
        </div>
      )}

      {/* Explicación */}
      {revealed && reveal.explanation && (
        <div className="surface p-5 max-w-3xl mx-auto animate-rise">
          <p className="text-ink-400 text-xs uppercase tracking-wider mb-1.5">Por qué</p>
          <p className="text-ink-100 leading-relaxed">{reveal.explanation}</p>
        </div>
      )}

      {/* Clasificación entre rondas */}
      {revealed && room.settings.showLeaderboardBetweenRounds && reveal.teamStandings && (
        <TeamStandings teams={reveal.teamStandings} myTeam={me?.team} />
      )}
      {revealed && room.settings.showLeaderboardBetweenRounds && reveal.standings.length > 0 && (
        <Standings standings={reveal.standings} myId={myId} />
      )}

      {/* Última pregunta: las posiciones se guardan para el podio. */}
      {revealed && (question?.index ?? 0) + 1 >= (question?.total ?? 0) && (
        <p className="text-center text-ink-400 text-sm animate-rise">
          {isHost || room.serverHosted
            ? 'Las posiciones se revelan en el podio.'
            : 'Las posiciones se revelan en el podio. Espera al anfitrión.'}
        </p>
      )}

      {/* Controles del anfitrión */}
      {isHost && (
        <div className="sticky bottom-4 flex justify-center">
          {room.status === 'question' ? (
            <button
              onClick={onSkip}
              className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-100 transition-colors shadow-xl"
            >
              <SkipForward className="w-5 h-5" />
              Cerrar pregunta
            </button>
          ) : (
            <button
              onClick={onNext}
              className="flex items-center gap-2 px-8 py-4 rounded-xl font-bold bg-ember-500 hover:bg-ember-400 text-on-accent transition-colors shadow-xl shadow-ember-950/40"
            >
              {(question?.index ?? 0) + 1 >= (question?.total ?? 0) ? 'Ver podio' : 'Siguiente'}
              <ArrowRight className="w-5 h-5" />
            </button>
          )}
        </div>
      )}

      {!isHost && !room.serverHosted && room.status === 'reveal' && (
        <p className="text-center text-ink-500 text-sm">Esperando al anfitrión…</p>
      )}

      {/* Sin anfitrión, la sala avanza sola: se dice cuándo. */}
      {room.serverHosted && room.status === 'reveal' && autoLeft !== null && (
        <div className="sticky bottom-4 flex justify-center">
          <p className="flex items-center gap-2 px-5 py-2.5 rounded-xl surface-raised text-sm text-ink-200 shadow-xl tabular-nums">
            <Bot className="w-4 h-4 text-sky-400" />
            {(question?.index ?? 0) + 1 >= (question?.total ?? 0)
              ? `Podio en ${Math.ceil(autoLeft / 1000)} s`
              : `Siguiente pregunta en ${Math.ceil(autoLeft / 1000)} s`}
          </p>
        </div>
      )}

      {confirmLeave && (
        <LeaveDialog isHost={isHost} onCancel={() => setConfirmLeave(false)} onConfirm={onLeave} />
      )}
    </div>
  );
};

/**
 * Confirmación al abandonar.
 *
 * Se pregunta porque salir a mitad de partida pierde el puntaje, y al
 * anfitrión se le advierte de algo que no es obvio: nadie más puede pasar de
 * pregunta, así que si se va, la partida se queda parada para todos.
 */
const LeaveDialog: React.FC<{
  isHost: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ isHost, onCancel, onConfirm }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-950/80 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={(e) => e.target === e.currentTarget && onCancel()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="leave-title"
    >
      <div className="surface-raised w-full max-w-sm p-6 space-y-4 animate-pop">
        <h2 id="leave-title" className="font-display font-bold text-lg">
          ¿Salir de la partida?
        </h2>
        <p className="text-ink-300 text-sm leading-relaxed">
          {isHost
            ? 'Eres el anfitrión: si te vas, nadie podrá pasar a la siguiente pregunta y la partida se quedará parada.'
            : 'Perderás el puntaje de esta partida.'}
        </p>
        <div className="flex gap-3">
          <button
            onClick={onCancel}
            autoFocus
            className="flex-1 py-3 rounded-xl font-semibold bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-100 transition-colors"
          >
            Seguir jugando
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-3 rounded-xl font-semibold bg-danger-500 hover:bg-danger-400 text-white transition-colors"
          >
            Salir
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ *
 * Sub-vistas
 * ------------------------------------------------------------------ */

/**
 * PIN fijo en una esquina durante toda la partida.
 *
 * En el lobby ocupa el centro de la pantalla, pero al empezar desaparecía, y
 * quien llegaba tarde (o se caía y tenía que volver a entrar) no tenía de
 * dónde sacarlo. Va abajo a la izquierda para no tapar el cronómetro ni los
 * controles del anfitrión, que están arriba y abajo al centro.
 */
const PinBadge: React.FC<{ pin: string }> = ({ pin }) => (
  <div
    className="fixed bottom-3 left-3 z-40 flex items-center gap-2 px-3 py-1.5 rounded-xl surface-raised shadow-lg pointer-events-none"
    aria-label={`PIN de la sala ${pin.split('').join(' ')}`}
  >
    <span className="text-ink-400 text-[10px] uppercase tracking-[0.15em]">PIN</span>
    <span className="font-mono text-lg sm:text-xl font-black tracking-[0.12em] text-brand-gradient tabular-nums">
      {pin}
    </span>
  </div>
);

const ConnectionBanner: React.FC<{ status: ConnectionStatus }> = ({ status }) => (
  <div
    role="status"
    className="flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl bg-ember-500/15 border border-ember-600 text-ember-300 text-sm"
  >
    {status === 'reconnecting' || status === 'connecting' ? (
      <>
        <Loader2 className="w-4 h-4 animate-spin" />
        Reconectando… tu puntaje está a salvo.
      </>
    ) : (
      <>
        <WifiOff className="w-4 h-4" />
        Sin conexión.
      </>
    )}
  </div>
);

/**
 * Pantalla de espera tras contestar.
 *
 * Quien ya respondió se quedaba mirando su propia respuesta sin señal de
 * cuánto faltaba. Aquí ve el progreso real de la sala, así que la espera deja
 * de ser tiempo muerto: sabe si faltan diez personas o una.
 */
const WaitingForOthers: React.FC<{ answered: number; total: number }> = ({ answered, total }) => {
  const pending = Math.max(0, total - answered);
  const progress = total > 0 ? answered / total : 1;

  return (
    <div className="animate-pop max-w-md mx-auto surface-raised p-6 text-center space-y-4">
      <div className="flex items-center justify-center gap-3">
        {/* El íbice "pastando" mientras se espera: la marca aparece justo en el
            momento muerto, en vez de un indicador genérico de carga. */}
        <AppIcon size={44} className="animate-bounce-slow" />
        <div className="text-left">
          <p className="font-display font-bold text-ink-50 leading-tight">Respuesta enviada</p>
          <p className="text-ink-400 text-sm">
            {pending === 0
              ? 'Ya contestaron todos'
              : pending === 1
                ? 'Falta 1 persona'
                : `Faltan ${pending} personas`}
          </p>
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="h-2 rounded-full bg-ink-800 overflow-hidden">
          <div
            className="h-full bg-brand-gradient transition-[width] duration-500 ease-out"
            style={{ width: `${progress * 100}%` }}
          />
        </div>
        <p className="text-ink-500 text-xs font-mono tabular-nums">
          {answered} de {total} han contestado
        </p>
      </div>
    </div>
  );
};

const PersonalResult: React.FC<{ you: NonNullable<RevealPayload['you']> }> = ({ you }) => (
  <div
    className={`animate-pop max-w-md mx-auto rounded-2xl border-2 p-5 text-center ${
      you.isCorrect
        ? 'bg-success-500/12 border-success-500'
        : 'bg-danger-500/10 border-danger-500'
    }`}
  >
    <p
      className={`text-2xl font-display font-bold ${
        you.isCorrect ? 'text-success-400' : 'text-danger-400'
      }`}
    >
      {you.isCorrect ? '¡Correcto!' : 'Incorrecto'}
    </p>
    {you.isCorrect && (
      <p className="text-ink-100 mt-1">
        <span className="text-ember-300 font-bold text-xl">+{you.pointsAwarded}</span>
        <span className="text-ink-400 text-sm"> en {(you.responseTimeMs / 1000).toFixed(1)}s</span>
      </p>
    )}
    {you.streak > 1 && (
      <p className="text-ember-300 text-sm mt-1.5 flex items-center justify-center gap-1">
        <Flame className="w-4 h-4" /> Racha de {you.streak}
      </p>
    )}
  </div>
);

/** Aviso de supervisión: los jugadores deben saber que se les graba. */
const MonitorNotice: React.FC<{ isHost: boolean; compact?: boolean }> = ({ isHost, compact }) => (
  <div
    role="status"
    className="flex items-start gap-3 rounded-xl border border-sky-700 bg-sky-500/10 px-4 py-3 text-sm text-sky-100 max-w-2xl mx-auto"
  >
    <Eye className="w-5 h-5 text-sky-400 shrink-0 mt-0.5" />
    <p>
      {isHost
        ? 'Esta sala está supervisada: se graba lo que los jugadores hacen en Open Dahu y lo verás en vivo y en Grabaciones. Ellos ven este aviso.'
        : compact
          ? 'Sala supervisada: el anfitrión puede ver y grabar tu pantalla de Open Dahu.'
          : 'Esta sala está supervisada. El anfitrión puede ver y grabar lo que haces en Open Dahu (clics y pantalla de esta pestaña, nunca otras pestañas ni apps) para detectar trampas. Si no estás de acuerdo, sal antes de empezar.'}
    </p>
  </div>
);

const TeamStandings: React.FC<{ teams: TeamStanding[]; myTeam?: string }> = ({
  teams,
  myTeam,
}) => (
  <div className="surface p-4 max-w-2xl mx-auto">
    <h3 className="text-sm uppercase tracking-wider text-ink-400 mb-3">Clasificación por equipos</h3>
    <ol className="space-y-1.5">
      {teams.map((t, i) => {
        const style = TEAM_COLORS[t.team as TeamId];
        return (
          <li
            key={t.team}
            className={`animate-rise flex items-center gap-3 px-3 py-2 rounded-lg ${
              t.team === (myTeam ?? '') ? 'bg-sky-500/15 border border-sky-700' : 'bg-ink-850'
            }`}
            style={{ animationDelay: `${i * 40}ms` }}
          >
            <span
              className={`w-6 text-center font-bold ${i < 3 ? 'text-ember-400' : 'text-ink-500'}`}
            >
              {i + 1}
            </span>
            <span className={`chip ${style?.chip ?? 'border-ink-600 bg-ink-800 text-ink-300'}`}>
              {style?.label ?? (t.team || 'Sin equipo')}
            </span>
            <span className="flex-1 text-ink-500 text-xs">
              {t.members} {t.members === 1 ? 'jugador' : 'jugadores'}
            </span>
            {t.delta > 0 && <span className="text-success-400 text-sm font-mono">+{t.delta}</span>}
            <span className="font-mono tabular-nums text-ink-200 font-semibold">
              {t.score.toLocaleString('es')}
            </span>
          </li>
        );
      })}
    </ol>
  </div>
);

const Standings: React.FC<{ standings: RevealPayload['standings']; myId: string }> = ({
  standings,
  myId,
}) => (
  <div className="surface p-4 max-w-2xl mx-auto">
    <h3 className="text-sm uppercase tracking-wider text-ink-400 mb-3">Clasificación</h3>
    <ol className="space-y-1.5">
      {standings.map((p, i) => (
        <li
          key={p.id}
          className={`animate-rise flex items-center gap-3 px-3 py-2 rounded-lg ${
            p.id === myId ? 'bg-sky-500/15 border border-sky-700' : 'bg-ink-850'
          }`}
          style={{ animationDelay: `${i * 40}ms` }}
        >
          <span
            className={`w-6 text-center font-bold ${i < 3 ? 'text-ember-400' : 'text-ink-500'}`}
          >
            {i + 1}
          </span>
          <Avatar config={p.avatar} size={36} />
          <span className="flex-1 min-w-0 truncate text-ink-100 font-medium">{p.displayName}</span>
          {p.streak > 1 && (
            <span className="flex items-center gap-0.5 text-ember-300 text-xs font-semibold">
              <Flame className="w-3.5 h-3.5" /> {p.streak}
            </span>
          )}
          {p.delta > 0 && <span className="text-success-400 text-sm font-mono">+{p.delta}</span>}
          <span className="font-mono tabular-nums text-ink-200 font-semibold">
            {p.score.toLocaleString('es')}
          </span>
        </li>
      ))}
    </ol>
  </div>
);

/* ---------------- Lobby ---------------- */

const LobbyView: React.FC<{
  room: PublicRoom;
  isHost: boolean;
  myId: string;
  connection: ConnectionStatus;
  onStart: () => void;
  onKick: (id: string) => void;
  onSetTeam: (team: string) => void;
  onLeave: () => void;
  onReady: (ready: boolean) => void;
  onSchedule: (startAt: number | null) => void;
  friends: FriendSummary[] | null;
  canInvite: boolean;
  onManageFriends: () => void;
}> = ({
  room,
  isHost,
  myId,
  connection,
  onStart,
  onKick,
  onSetTeam,
  onLeave,
  onReady,
  onSchedule,
  friends,
  canInvite,
  onManageFriends,
}) => {
  const [copied, setCopied] = useState(false);
  const players = room.players.filter((p) => p.id !== room.hostId);
  const full = players.length >= room.settings.maxPlayers;
  const server = room.serverHosted;
  const me = room.players.find((p) => p.id === myId);
  const readyCount = players.filter((p) => p.connected && p.ready).length;
  const presentCount = players.filter((p) => p.connected).length;
  /** Quién maneja la hora de inicio: el anfitrión, o quien abrió una sala del servidor. */
  const canSchedule = server ? room.creatorId === myId : isHost;
  const startLeft = useCountdown(room.startAt);
  const autoLeft = useCountdown(server ? room.autoAt : null);

  const copyPin = async () => {
    try {
      await navigator.clipboard.writeText(room.pin);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* sin permiso de portapapeles: el PIN está a la vista de todos modos */
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8 space-y-6 sm:space-y-8 relative">
      <div className="flex justify-end sm:-mb-6">
        <AudioControls labeled align="right" className="z-10" />
      </div>
      {connection !== 'online' && <ConnectionBanner status={connection} />}

      {room.settings.monitorPlayers && <MonitorNotice isHost={isHost} />}

      {/* PIN */}
      <div className="text-center space-y-3">
        <p className="text-ink-400 uppercase tracking-[0.2em] text-xs sm:text-sm">
          Entra en {location.host} con el PIN
        </p>
        <button
          onClick={copyPin}
          className="group inline-flex items-center gap-3 sm:gap-4 px-5 sm:px-8 py-4 sm:py-5 rounded-3xl surface-raised hover:border-sky-600 transition-colors"
          aria-label={`PIN de la sala ${room.pin.split('').join(' ')}. Pulsa para copiar.`}
        >
          <span className="font-mono text-4xl sm:text-5xl md:text-7xl font-black tracking-[0.12em] sm:tracking-[0.15em] text-brand-gradient">
            {room.pin}
          </span>
          {copied ? (
            <Check className="w-6 h-6 text-success-400" />
          ) : (
            <Copy className="w-6 h-6 text-ink-500 group-hover:text-ink-300 transition-colors" />
          )}
        </button>
        <p className="text-ink-300 text-base sm:text-lg text-balance">{room.quizTitle}</p>
        {server && (
          <span className="chip border-sky-600 bg-sky-500/10 text-sky-300">
            <Bot className="w-3.5 h-3.5" />
            Sin anfitrión · creada por {room.creatorName}
          </span>
        )}

        {room.settings.prizePool > 0 && (
          <span className="chip border-ember-500 bg-ember-500/15 text-ember-300">
            <Coins className="w-3.5 h-3.5" />
            Bote de {room.settings.prizePool.toLocaleString('es')} {BRAND.currencyName}
          </span>
        )}
        {room.settings.hostPlays && (
          <span className="chip border-sky-500 bg-sky-500/15 text-sky-300">
            <Gamepad2 className="w-3.5 h-3.5" />
            {isHost
              ? 'Juegas tú también'
              : room.settings.prizePool > 0
                ? 'El anfitrión también juega y compite por el bote'
                : 'El anfitrión también juega'}
          </span>
        )}
      </div>

      {/* La IA aún escribe la trivia */}
      {room.preparing && (
        <div role="status" className="max-w-xl mx-auto flex items-center gap-3 rounded-2xl border border-sky-700 bg-sky-500/10 px-5 py-4">
          <Sparkles className="w-6 h-6 text-sky-400 shrink-0 animate-pulse" />
          <div>
            <p className="font-semibold text-ink-50">{room.preparing}</p>
            <p className="text-sm text-ink-400">Podéis ir entrando: la partida estará lista en un momento.</p>
          </div>
        </div>
      )}

      {/* Hora de inicio fijada: la ve todo el mundo */}
      {room.startAt && startLeft !== null && (
        <div role="timer" className="max-w-md mx-auto text-center rounded-2xl border-2 border-ember-500 bg-ember-500/10 px-5 py-4 space-y-1">
          <p className="text-ember-300 text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> Empieza a las {localClockOf(room.startAt)}
          </p>
          <p className="font-mono text-5xl sm:text-6xl font-black tabular-nums text-ink-50">
            {formatCountdown(startLeft)}
          </p>
        </div>
      )}

      {/* Arranque por "listos" en salas del servidor */}
      {server && !room.startAt && autoLeft !== null && (
        <div role="timer" className="max-w-md mx-auto text-center rounded-2xl border-2 border-success-500 bg-success-500/10 px-5 py-3">
          <p className="text-success-400 text-sm font-semibold">
            {readyCount === presentCount ? '¡Todos listos!' : `${readyCount} de ${presentCount} listos`} · empieza en{' '}
            <span className="font-mono tabular-nums">{Math.ceil(autoLeft / 1000)} s</span>
          </p>
        </div>
      )}

      {/* Programar la hora de inicio */}
      {canSchedule && <ScheduleControls room={room} onSchedule={onSchedule} />}

      {/* Equipos */}
      {room.settings.gameMode === 'teams' && !isHost && (
        <div className="space-y-2">
          <p className="text-center text-ink-400 text-sm">Elige equipo</p>
          <div className="flex justify-center gap-2 flex-wrap">
            {TEAM_IDS.map((id: TeamId) => {
              const team = TEAM_COLORS[id];
              const mine = room.players.find((p) => p.id === myId)?.team === id;
              return (
                <button
                  key={id}
                  onClick={() => onSetTeam(id)}
                  className={`px-5 py-2.5 rounded-xl border-2 font-semibold transition-all ${team.chip} ${
                    mine ? 'ring-2 ring-offset-2 ring-offset-ink-950 ring-white/60 scale-105' : 'opacity-70 hover:opacity-100'
                  }`}
                >
                  {team.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Jugadores */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg sm:text-xl font-bold flex items-center gap-2">
            <Users className="w-5 h-5 text-sky-400" />
            {players.length} {players.length === 1 ? 'jugador' : 'jugadores'}
            <span className="text-ink-500 text-sm font-normal">
              / {room.settings.maxPlayers} máx.
            </span>
          </h2>
          {full && <span className="chip border-ember-600 text-ember-300 bg-ember-500/10">Sala llena</span>}
        </div>

        {players.length === 0 ? (
          <div className="surface p-12 text-center">
            <div className="shimmer h-2 w-32 mx-auto rounded-full mb-4" />
            <p className="text-ink-400">Esperando a que alguien entre con el PIN…</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {players.map((p) => (
              <div
                key={p.id}
                className={`animate-pop surface p-3 flex flex-col items-center gap-2 relative ${
                  p.connected ? '' : 'opacity-50'
                }`}
              >
                {isHost && (
                  <button
                    onClick={() => onKick(p.id)}
                    className="absolute top-1.5 right-1.5 p-1.5 rounded-lg text-ink-600 hover:text-danger-400 hover:bg-danger-500/10 transition-colors"
                    aria-label={`Expulsar a ${p.displayName}`}
                  >
                    <UserX className="w-4 h-4" />
                  </button>
                )}
                <Avatar config={p.avatar} size={48} />
                <span className="text-sm font-medium text-ink-100 truncate max-w-full">
                  {p.displayName}
                </span>
                {p.team && (
                  <span className={`chip text-[10px] ${TEAM_COLORS[p.team as TeamId]?.chip ?? ''}`}>
                    {TEAM_COLORS[p.team as TeamId]?.label ?? p.team}
                  </span>
                )}
                {server && (
                  <span
                    className={`flex items-center gap-1 text-[11px] font-medium ${
                      p.ready ? 'text-success-400' : 'text-ink-500'
                    }`}
                  >
                    {p.ready ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                    {p.ready ? 'Listo' : 'Esperando'}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invitar a amigos: cualquiera con cuenta puede traer a los suyos */}
      {canInvite && (
        <InviteFriendsPanel
          friends={friends}
          inRoomIds={room.players.map((p) => p.id)}
          onManageFriends={onManageFriends}
        />
      )}

      {/* Acciones */}
      {server ? (
        <div className="flex flex-col items-center gap-3">
          <button
            onClick={() => onReady(!me?.ready)}
            disabled={Boolean(room.preparing)}
            aria-pressed={Boolean(me?.ready)}
            className={`flex items-center gap-2 px-8 py-3.5 rounded-xl font-bold transition-colors shadow-lg disabled:bg-ink-800 disabled:text-ink-500 ${
              me?.ready
                ? 'bg-success-500/15 border-2 border-success-500 text-success-400 hover:bg-success-500/25'
                : 'bg-ember-500 hover:bg-ember-400 text-on-accent shadow-ember-950/40'
            }`}
          >
            {me?.ready ? <CheckCircle2 className="w-5 h-5" /> : <Play className="w-5 h-5" fill="currentColor" />}
            {me?.ready ? 'Listo · pulsa para esperar' : 'Estoy listo'}
          </button>
          <p className="text-ink-500 text-xs text-center max-w-sm">
            {room.startAt
              ? 'La partida empieza a la hora fijada, estéis listos o no.'
              : 'Si todos están listos empieza a los 3 segundos; si lo está al menos la mitad, a los 30.'}
          </p>
          <button
            onClick={onLeave}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-ink-400 hover:text-ink-100 hover:bg-ink-850 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Salir de la sala
          </button>
        </div>
      ) : isHost ? (
        <div className="flex justify-center gap-3">
          <button
            onClick={onLeave}
            className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-200 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Salir
          </button>
          <button
            onClick={onStart}
            disabled={players.length === 0}
            className="flex items-center gap-2 px-8 py-3 rounded-xl font-bold bg-ember-500 hover:bg-ember-400 text-on-accent disabled:bg-ink-800 disabled:text-ink-500 disabled:cursor-not-allowed transition-colors shadow-lg shadow-ember-950/40"
          >
            <Play className="w-5 h-5" fill="currentColor" />
            {players.length === 0 ? 'Falta gente' : 'Empezar'}
          </button>
        </div>
      ) : (
        // El jugador no puede hacer nada más que esperar: se le dice en una
        // línea y la salida queda discreta debajo, en vez de un botón gordo
        // al lado de un texto que en el teléfono se partía en dos.
        <div className="flex flex-col items-center gap-3">
          <p className="flex items-center gap-2 text-ink-300 text-sm">
            <Crown className="w-4 h-4 text-ember-400" />
            {room.startAt ? 'La partida empieza sola a la hora fijada' : 'El anfitrión empieza la partida'}
          </p>
          <button
            onClick={onLeave}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-ink-400 hover:text-ink-100 hover:bg-ink-850 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Salir de la sala
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Programar el inicio desde dentro de la sala: un temporizador o una hora.
 * Solo lo ve quien puede cambiarlo; la cuenta atrás, en cambio, la ven todos.
 */
const ScheduleControls: React.FC<{
  room: PublicRoom;
  onSchedule: (startAt: number | null) => void;
}> = ({ room, onSchedule }) => {
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState<StartChoice>({ ...DEFAULT_START, mode: 'timer' });

  if (!open) {
    return (
      <div className="flex justify-center gap-2 flex-wrap">
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border border-ink-600 bg-ink-850 text-ink-200 hover:border-ink-500 transition-colors"
        >
          <Clock className="w-4 h-4 text-ember-400" />
          {room.startAt ? 'Cambiar la hora de inicio' : 'Programar inicio o temporizador'}
        </button>
        {room.startAt && (
          <button
            onClick={() => onSchedule(null)}
            className="px-4 py-2 rounded-xl text-sm font-medium text-ink-400 hover:text-ink-100 hover:bg-ink-850 transition-colors"
          >
            Quitar la hora
          </button>
        )}
      </div>
    );
  }

  const at = startAtFromChoice(choice);
  return (
    <div className="surface p-4 space-y-3 max-w-md mx-auto">
      <StartTimePicker
        value={choice}
        onChange={setChoice}
        manualLabel="Sin hora"
        manualHint={room.serverHosted ? 'Empieza cuando la gente esté lista.' : 'La empiezas tú con el botón.'}
      />
      <div className="flex gap-2">
        <button
          onClick={() => setOpen(false)}
          className="flex-1 h-10 rounded-lg text-sm font-semibold bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-200"
        >
          Cancelar
        </button>
        <button
          onClick={() => {
            onSchedule(at);
            setOpen(false);
          }}
          className="flex-[2] h-10 rounded-lg text-sm font-bold bg-ember-500 hover:bg-ember-400 text-on-accent"
        >
          {at === null ? 'Quitar la hora' : 'Fijar'}
        </button>
      </div>
    </div>
  );
};
